import React, { useState, useEffect, lazy } from 'react';
import { motion } from 'framer-motion';
import { Users, Search, RefreshCw, Mail, Shield, AlertTriangle, CheckCircle, XCircle, TrendingUp, Activity, BarChart3, Lock, Unlock, ChevronLeft, ChevronRight } from 'lucide-react';
import { adminService } from '../../services/adminService';

import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import { LoadingState } from '../../components/ui/LoadingStates';


const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'job_seeker', label: 'Job Seeker' },
  { value: 'hiring_manager', label: 'Hiring Manager' },
  { value: 'company_admin', label: 'Company Admin' },
  { value: 'admin', label: 'Admin' }
];

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'locked', label: 'Locked' }
];

const ACTION_TYPES = [
  { value: 'user_suspend', label: 'Suspend Account', icon: Lock, color: 'text-red-600' },
  { value: 'user_activate', label: 'Activate Account', icon: Unlock, color: 'text-green-600' },
  { value: 'email_verify', label: 'Verify Email', icon: Mail, color: 'text-blue-600' },
  { value: 'role_change', label: 'Change Role', icon: Shield, color: 'text-purple-600' },
  { value: 'assign_subscription', label: 'Assign Subscription Plan', icon: TrendingUp, color: 'text-indigo-600' }
];

export default function AdminUserManagementSystem() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [processingAction, setProcessingAction] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [accountStatusFilter, setAccountStatusFilter] = useState('all');
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 20;

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [roleFilter, accountStatusFilter, emailVerifiedFilter, searchQuery]);

  const loadAdminData = async () => {
    try {
      const [profile, stats] = await Promise.all([
        adminService?.getAdminProfile(),
        adminService?.getPlatformStatistics()
      ]);
      setAdminProfile(profile);
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load admin data');
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (roleFilter !== 'all') filters.role = roleFilter;
      if (accountStatusFilter !== 'all') filters.accountStatus = accountStatusFilter;
      if (emailVerifiedFilter !== 'all') filters.emailVerified = emailVerifiedFilter === 'verified';
      if (searchQuery) filters.search = searchQuery;

      const data = await adminService?.getAllUsers(filters);
      setUsers(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatistics = async () => {
    try {
      await adminService?.updatePlatformStatistics();
      const stats = await adminService?.getPlatformStatistics();
      setStatistics(stats);
      setSuccess('Statistics updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update statistics');
    }
  };

  const handleUserAction = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setActionReason('');
    setShowActionModal(true);
  };

  const executeUserAction = async () => {
    if (!actionReason?.trim()) {
      setError('Please provide a reason for this action');
      return;
    }

    try {
      setProcessingAction(true);
      setError('');

      switch (actionType) {
        case 'user_suspend':
          await adminService?.suspendUser(selectedUser?.id, actionReason);
          setSuccess('User account suspended successfully');
          break;
        case 'user_activate':
          await adminService?.activateUser(selectedUser?.id, actionReason);
          setSuccess('User account activated successfully');
          break;
        case 'email_verify':
          await adminService?.verifyUserEmail(selectedUser?.id, actionReason);
          setSuccess('User email verified successfully');
          break;
        case 'role_change':
          // Would need additional UI for role selection
          setError('Role change requires additional parameters');
          break;
        case 'assign_subscription':
          // Would need additional UI for plan selection and company ID
          setError('Subscription assignment requires additional parameters');
          break;
        default:
          setError('Unknown action type');
      }

      setShowActionModal(false);
      await loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message);
    } finally {
      setProcessingAction(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      job_seeker: 'bg-blue-100 text-blue-800',
      hiring_manager: 'bg-green-100 text-green-800',
      company_admin: 'bg-purple-100 text-purple-800',
      admin: 'bg-red-100 text-red-800'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-800';
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users?.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(users?.length / usersPerPage);

  if (loading && !users?.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">User Management System</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Comprehensive user administration and oversight
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleRefreshStatistics}
                className="flex items-center space-x-2"
              >
                <RefreshCw size={16} />
                <span>Refresh Stats</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Success/Error Messages */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2"
          >
            <CheckCircle className="text-green-600" size={20} />
            <span className="text-green-800">{success}</span>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2"
          >
            <XCircle className="text-red-600" size={20} />
            <span className="text-red-800">{error}</span>
          </motion.div>
        )}

        {/* Platform Statistics */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {statistics?.total_users?.count || 0}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {statistics?.total_users?.active || 0} active
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="text-blue-600" size={24} />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Support Tickets</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {statistics?.support_tickets?.total || 0}
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    {statistics?.support_tickets?.new || 0} new
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Activity className="text-orange-600" size={24} />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Job Postings</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {statistics?.job_postings?.total || 0}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {statistics?.job_postings?.active || 0} active
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-green-600" size={24} />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Applications</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {statistics?.applications?.total || 0}
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    {statistics?.applications?.pending || 0} pending
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="text-purple-600" size={24} />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Search Users
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Name or email..."
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {ROLE_OPTIONS?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Account Status
              </label>
              <select
                value={accountStatusFilter}
                onChange={(e) => setAccountStatusFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {ACCOUNT_STATUS_OPTIONS?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Verified
              </label>
              <select
                value={emailVerifiedFilter}
                onChange={(e) => setEmailVerifiedFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Users Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Email Verified
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-border">
                {currentUsers?.map((user) => (
                  <tr key={user?.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user?.avatarUrl ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user?.avatarUrl}
                              alt={user?.fullName}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-medium">
                                {user?.fullName?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-foreground">
                            {user?.fullName}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user?.role)}`}>
                        {user?.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user?.accountLocked ? (
                        <span className="flex items-center text-red-600">
                          <Lock size={14} className="mr-1" />
                          Locked
                        </span>
                      ) : (
                        <span className="flex items-center text-green-600">
                          <Unlock size={14} className="mr-1" />
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user?.emailVerified ? (
                        <span className="flex items-center text-green-600">
                          <CheckCircle size={14} className="mr-1" />
                          Verified
                        </span>
                      ) : (
                        <span className="flex items-center text-orange-600">
                          <AlertTriangle size={14} className="mr-1" />
                          Unverified
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(user.createdAt)?.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {user?.accountLocked ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUserAction(user, 'user_activate')}
                          >
                            <Unlock size={14} className="mr-1" />
                            Activate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUserAction(user, 'user_suspend')}
                          >
                            <Lock size={14} className="mr-1" />
                            Suspend
                          </Button>
                        )}
                        {!user?.emailVerified && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUserAction(user, 'email_verify')}
                          >
                            <Mail size={14} className="mr-1" />
                            Verify
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-border flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, users?.length)} of {users?.length} users
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-sm text-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
          >
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {ACTION_TYPES?.find(a => a?.value === actionType)?.label}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                User: <span className="font-medium text-foreground">{selectedUser?.fullName}</span>
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Reason (Required)
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e?.target?.value)}
                  placeholder="Provide a detailed reason for this action..."
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  rows={4}
                />
              </div>
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowActionModal(false)}
                  disabled={processingAction}
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeUserAction}
                  disabled={processingAction || !actionReason?.trim()}
                >
                  {processingAction ? 'Processing...' : 'Confirm Action'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}